/**
 * Skåpinställningar per organisation (toppnod)
 * Aktivera skåp + redigerbar unik SuperAdmin-PIN (KeyWin).
 */

import { useEffect, useState, useCallback } from 'react';
import { EyeIcon, EyeSlashIcon, ClipboardDocumentIcon, ArrowPathIcon, ArchiveBoxIcon } from '@heroicons/react/24/outline';
import { useTranslation } from '@/store/languageStore';
import { lockerService } from '@/services/locker/lockerService';
import type { CabinetCustomerAssignment, CabinetSettings } from '@/types/locker';
import toast from 'react-hot-toast';

interface Props {
  organizationId: string;
}

export default function OrganizationCabinetSettings({ organizationId }: Props) {
  const { t } = useTranslation();
  const [settings, setSettings] = useState<CabinetSettings | null>(null);
  const [cabinets, setCabinets] = useState<CabinetCustomerAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pinSaving, setPinSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [showPin, setShowPin] = useState(false);
  const [pinDraft, setPinDraft] = useState('');
  const [syncingCabinetId, setSyncingCabinetId] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!organizationId) return;
    setLoading(true);
    try {
      const [settingsData, cabRes] = await Promise.all([
        lockerService.getCabinetSettings(organizationId).catch(() => null),
        lockerService.getCabinetCustomerAssignments(organizationId).catch(() => []),
      ]);
      setSettings(settingsData);
      setPinDraft(settingsData?.super_admin_pin ?? '');
      setCabinets(cabRes);
    } catch {
      setSettings(null);
      setPinDraft('');
      setCabinets([]);
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => { load(); }, [load]);

  const copyPin = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(t('organizations.cabinetPinCopied'));
    } catch {
      const el = document.createElement('textarea');
      el.value = value;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      toast.success(t('organizations.cabinetPinCopied'));
    }
  };

  const syncCabinetTags = async (cabinetId: string) => {
    setSyncingCabinetId(cabinetId);
    try {
      const result = await lockerService.syncCabinetAdminKeys(cabinetId);
      if (result.connected === 0) {
        toast(t('organizations.cabinetSyncTagsEmpty'));
      } else {
        toast.success(t('organizations.cabinetSyncTagsSuccess', { count: result.connected }));
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error ?? t('organizations.cabinetSyncTagsError'));
    } finally {
      setSyncingCabinetId(null);
    }
  };

  const handleToggleHasCabinet = async () => {
    setSaving(true);
    try {
      if (settings?.has_cabinet) {
        const next = await lockerService.disableCabinetSettings(organizationId);
        setSettings(next);
        setPinDraft(next?.super_admin_pin ?? '');
        toast.success(t('organizations.cabinetDisabled'));
      } else {
        const next = await lockerService.enableCabinetSettings(organizationId);
        setSettings(next);
        setPinDraft(next?.super_admin_pin ?? '');
        setShowPin(true);
        toast.success(t('organizations.cabinetEnabled'));
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error ?? t('organizations.cabinetEnableError'));
    } finally {
      setSaving(false);
    }
  };

  const applyPinResult = (next: CabinetSettings) => {
    setSettings(next);
    setPinDraft(next.super_admin_pin ?? '');
    setShowPin(true);
  };

  const handleSavePin = async () => {
    const digits = pinDraft.replace(/\D/g, '');
    if (digits.length !== 8) {
      toast.error(t('organizations.cabinetPinInvalid'));
      return;
    }
    setPinSaving(true);
    try {
      const next = await lockerService.updateSuperAdminPin(organizationId, { pin: digits });
      applyPinResult(next);
      toast.success(t('organizations.cabinetPinSaved'));
    } catch (error: any) {
      toast.error(error.response?.data?.error ?? t('organizations.cabinetPinSaveError'));
    } finally {
      setPinSaving(false);
    }
  };

  const handleGeneratePin = async () => {
    setGenerating(true);
    try {
      const next = await lockerService.updateSuperAdminPin(organizationId, { generate: true });
      applyPinResult(next);
      toast.success(t('organizations.cabinetPinGenerated'));
    } catch (error: any) {
      toast.error(error.response?.data?.error ?? t('organizations.cabinetPinSaveError'));
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="p-4 rounded-lg border border-border bg-bgElevated">
        <div className="animate-pulse space-y-4">
          <div className="h-5 bg-bgBase rounded w-1/3" />
          <div className="h-10 bg-bgBase rounded w-full" />
        </div>
      </div>
    );
  }

  const hasCabinet = Number(settings?.has_cabinet) === 1;
  const pinDirty = pinDraft.replace(/\D/g, '') !== (settings?.super_admin_pin ?? '');

  return (
    <div className="p-4 rounded-lg border border-border bg-bgElevated space-y-5">
      <h3 className="text-sm font-medium text-textDefault">{t('organizations.cabinetSettings')}</h3>

      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={hasCabinet}
          disabled={saving}
          onChange={handleToggleHasCabinet}
          className="mt-1"
        />
        <span>
          <span className="block text-sm font-medium text-textDefault">{t('organizations.cabinetHasCabinet')}</span>
          <span className="block text-xs text-textTertiary">{t('organizations.cabinetHasCabinetHelp')}</span>
        </span>
      </label>

      {hasCabinet && (
        <div>
          <label className="block text-sm font-medium text-textDefault mb-1">
            {t('organizations.cabinetSuperAdminPin')}
          </label>
          <div className="flex flex-wrap items-center gap-2 max-w-xl">
            <div className="relative flex-1 min-w-[10rem]">
              <input
                type={showPin ? 'text' : 'password'}
                inputMode="numeric"
                autoComplete="off"
                maxLength={8}
                value={pinDraft}
                onChange={(e) => setPinDraft(e.target.value.replace(/\D/g, '').slice(0, 8))}
                className="w-full px-3 py-2 pr-10 border border-border rounded-lg bg-bgContainer text-textDefault text-sm font-mono tracking-widest"
              />
              <button
                type="button"
                onClick={() => setShowPin(!showPin)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-textTertiary hover:text-textDefault transition"
              >
                {showPin ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
              </button>
            </div>
            <button
              type="button"
              onClick={() => { if (pinDraft) copyPin(pinDraft); }}
              disabled={!pinDraft}
              className="px-3 py-2 text-sm font-medium rounded-lg border border-border bg-bgContainer text-textDefault hover:bg-bgLight transition disabled:opacity-50"
            >
              <ClipboardDocumentIcon className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={handleGeneratePin}
              disabled={generating || pinSaving}
              className="px-3 py-2 text-sm font-medium rounded-lg border border-border bg-bgContainer text-textDefault hover:bg-bgLight transition flex items-center gap-1.5 disabled:opacity-50"
            >
              <ArrowPathIcon className={`w-4 h-4 ${generating ? 'animate-spin' : ''}`} />
              {t('organizations.cabinetGeneratePin')}
            </button>
            <button
              type="button"
              onClick={handleSavePin}
              disabled={pinSaving || generating || pinDraft.length !== 8}
              className="px-3 py-2 text-sm font-medium rounded-lg bg-primary text-white hover:bg-primary/90 transition disabled:opacity-50"
            >
              {pinDirty ? t('common.save') : t('organizations.cabinetPinSync')}
            </button>
          </div>
          <p className="mt-1 text-xs text-textTertiary">{t('organizations.cabinetSuperAdminPinHelp')}</p>
        </div>
      )}

      <div className="border-t border-border pt-4">
        <h4 className="text-sm font-medium text-textDefault mb-2">{t('organizations.cabinetLinkedCabinets')}</h4>
        {cabinets.length === 0 ? (
          <p className="text-sm text-textTertiary italic">{t('organizations.cabinetNoLinkedCabinets')}</p>
        ) : (
          <div className="space-y-1.5">
            {cabinets.map((cab) => (
              <div key={cab.id} className="flex items-center gap-2 px-3 py-2 bg-bgBase rounded-lg text-sm">
                <ArchiveBoxIcon className="w-4 h-4 text-textTertiary flex-shrink-0" />
                <span className="font-medium text-textDefault flex-1 min-w-0 truncate">{cab.cabinet_name || cab.cabinet_id}</span>
                <button
                  type="button"
                  onClick={() => syncCabinetTags(cab.cabinet_id)}
                  disabled={syncingCabinetId === cab.cabinet_id}
                  title={t('organizations.cabinetSyncTagsHelp')}
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-lg border border-border bg-bgContainer text-textDefault hover:bg-bgLight transition disabled:opacity-50"
                >
                  <ArrowPathIcon className={`w-3.5 h-3.5 ${syncingCabinetId === cab.cabinet_id ? 'animate-spin' : ''}`} />
                  {t('organizations.cabinetSyncTags')}
                </button>
              </div>
            ))}
            <p className="text-xs text-textTertiary">{t('organizations.cabinetSyncTagsHelp')}</p>
          </div>
        )}
      </div>
    </div>
  );
}
